// src/views/menage/RunCard.jsx — CHANTIER 42 Phase 3 (extraction) + DA (CHANTIER 65)
//
// Carte d'un menage_run + ses sous-composants d'affichage. Réutilisée par :
//   - MenageTodayView  (écran agent ménage + double-casquette orchestrateur)
//   - MenageSuiviView  (détail du jour orchestrateur)
//
// GRAMMAIRE DE CARTE UNIQUE (DESIGN.md §8 ter) : blanc franc, radius, pastille de
// statut sémantique, boutons « Voir la checklist » corail + « Signaler un incident »
// secondaire, sélecteur « Assigner à ». Direction Artistique = HABILLAGE SEUL :
// aucune logique/data touchée (props, handlers, eff status, gardes inchangés).
//
// NON-RÉGRESSION AGENT : l'écran ménage ne dépend QUE du rendu de RunCard ; le
// move ne change ni les props ni le comportement. Les helpers de date/visibilité
// (todayParisIso, nowParisHHMM, computeVisibleEmails, computeAssignOptions)
// restent dans MenageTodayView (NON déplacés).

import { useState, useEffect, Fragment } from "react"
import FeuilleConfirmation from '../../components/FeuilleConfirmation'
import { libelleException, actionRetourALaRegle } from '../../lib/menage-exception'
import { DS } from "../../lib/ui"
import { fetchLogInfo } from "../../lib/menage-log-info-cache"
import { getEffectiveRunStatus, isRunOpenForMenageToday } from "../../lib/menage-status-helpers"
import { isWithinIncidentWindow, hoursRemainingInIncidentWindow } from "../../lib/incidents-window"
import BlocAApporter from './BlocAApporter'
// Icônes trait fin (lucide-react direct — DESIGN.md §5 ; PAS via lib/Icon.jsx,
// partagé/conflit). Remplacement 1:1 des emojis, couleur de marque héritée/forcée.
import { heureCourte } from "../../lib/menage-frise-api"
import { heureEffective, formaterHeure, formaterDuree } from "../../lib/menage-frise"
import {
  ClipboardList, AlertTriangle, MapPin, Key, Clock, Users, User, RotateCcw,
  Info, CircleAlert, CircleCheck, Check, Sparkles, Lock, Eye,
} from "lucide-react"
// LOT FRISE CONTRÔLE — pastille œil + fenêtre 72 h. Props ADDITIVES (`controle`,
// `onControler`, `onOuvrirControle`) : les appelants qui ne les passent pas
// gardent EXACTEMENT le comportement historique (aucune pastille, aucun bouton).
import PastilleControle from "../../components/PastilleControle"
import { dansFenetreControle } from "../../lib/controle-fenetre"

// ── Helpers d'affichage (HABILLAGE, PURS) ───────────────────────────
// Le compte ménage/orchestrateur (session-role) ne porte PAS de champ prénom :
// on dérive un libellé lisible depuis l'email en repli, sans jamais casser (chaîne
// vide → l'appelant retombe sur l'email brut). Zéro accès données / réseau.
export function firstNameFromEmail(email) {
  if (!email || typeof email !== "string") return ""
  const local = email.split("@")[0] || ""
  const tok = (local.split(/[._+\-]/).filter(Boolean)[0] || local).replace(/[0-9]+/g, "")
  if (!tok) return ""
  return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase()
}

// C-1 (règle Dimitri 2026-08-19) : JAMAIS un email complet à l'écran. Le
// repli d'affichage = la partie avant l'arobase, capitalisée — même quand
// firstNameFromEmail ne dérive rien (local 100 % chiffres → le local brut).
export function libelleDepuisEmail(email) {
  const prenom = firstNameFromEmail(email)
  if (prenom) return prenom
  const local = String(email || "").split("@")[0]
  if (!local) return "Agent"
  return local.charAt(0).toUpperCase() + local.slice(1)
}
export function initialsFromEmail(email) {
  const n = firstNameFromEmail(email)
  if (n) return n.slice(0, 2).toUpperCase()
  const c = String(email || "?").replace(/[^a-zA-Z]/g, "")
  return (c.slice(0, 2) || "?").toUpperCase()
}

// Pastille de statut sémantique (DA) — libellé propre (sans emoji) + icône, sur le
// fond/teinte sémantique déjà calculé par getEffectiveRunStatus (eff.bg/eff.fg).
const STATUS_PILL = {
  scheduled:       { label: "À faire",            Ico: ClipboardList },
  in_progress:     { label: "En cours",           Ico: Sparkles },
  upcoming:        { label: "Prévu",              Ico: Clock },
  completed:       { label: "Terminé",            Ico: CircleCheck },
  past:            { label: "Passé",              Ico: Check },
  skipped:         { label: "Passé",              Ico: Check },
  ferme_fait:      { label: "Fermé · fait",       Ico: Lock },
  ferme_non_saisi: { label: "Fermé · non saisi",  Ico: AlertTriangle },
  anomalie:        { label: "Anomalie",           Ico: AlertTriangle },
}
function pillFor(eff) {
  const m = STATUS_PILL[eff?.key]
  if (m) return m
  const clean = String(eff?.label || "").replace(/^[^\p{L}]+/u, "").trim()
  return { label: clean || "—", Ico: Info }
}

// ── RunCard ─────────────────────────────────────────────────────────
// ADR-302 — `instructionsCount` est ADDITIVE et vaut 0 par défaut : tout
// appelant non modifié rend une carte BYTE-IDENTIQUE (aucune pastille). C'est
// aussi ce qui garantit l'inertie si la migration n'est pas appliquée sur
// l'environnement (compte toujours 0 → aucun rendu, aucun bruit terrain).
export function RunCard({ run, today, nowHHMM, showAgentBadge = false, isOwnRun = true, isOrchestrateur = false, guestRealTimes = null, logementNameFallback = undefined, assignOptions = [], regleAssignation = null, nomAgent = undefined, instructionsCount = 0, onReassign, onSignalIncident, onOpenChecklist, controle = undefined, onControler = undefined, onOuvrirControle = undefined, heureContrat = null }) {
  // Ce ménage déroge-t-il à la règle du logement ? `regleAssignation` est une
  // prop ADDITIVE : les appelants qui ne la passent pas gardent exactement le
  // comportement historique (aucun badge).
  const exception = libelleException(run, regleAssignation, nomAgent)
  // Revenir à la règle N'EST PAS un mécanisme nouveau : c'est une
  // réassignation vers l'agent que la règle désigne. On réutilise donc
  // `onReassign`, déjà cloisonné par la policy orchestrateur_assign.
  const retour = actionRetourALaRegle(run, regleAssignation, assignOptions)
  // Phase 4 PR #141 (ADR-077) — fetch infos logement via endpoint dédié
  // (RLS storage tightened bloque la lecture client direct de fbz8-cfg).
  const [info, setInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // ⚠️ NOTRE GRAMMAIRE, plus une boîte du navigateur (Dimitri 2026-08-22).
  const [confirmRetour, setConfirmRetour] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(null)
    fetchLogInfo(run.logement_id, run.scheduled_date)
      .then(data => { if (!cancelled) { setInfo(data); setLoading(false) } })
      .catch(e => { if (!cancelled) { setError(e.message || String(e)); setLoading(false) } })
    return () => { cancelled = true }
  }, [run.logement_id, run.scheduled_date])

  // PR #151 PARTIE D + AMENDEMENT P3 — statut effectif (status DB + scheduled_date
  // vs today + heure d'activation check-out).
  const eff = getEffectiveRunStatus(run, today, nowHHMM, info?.timing?.checkout_time)
  const pill = pillFor(eff)

  // ADR-084 : breadcrumb Sentry si anomalie (run completed avant la date prévue).
  useEffect(() => {
    if (eff.key === 'anomalie' && typeof window !== 'undefined' && window.Sentry?.captureMessage) {
      window.Sentry.captureMessage('Menage run anomalie detected', {
        level: 'warning',
        extra: { run_id: run.id, status: run.status, scheduled_date: run.scheduled_date, today },
      })
    }
  }, [eff.key, run.id, run.status, run.scheduled_date, today])

  // PR #153 (ADR-088) — fenêtre de signalement tardive (48h) après completed_at.
  const withinIncidentWindow = isWithinIncidentWindow(run)
  const hoursRemaining = withinIncidentWindow ? hoursRemainingInIncidentWindow(run) : 0

  // ADR-230 — bouton « Signaler » actif le jour J (00:01) OU dans la fenêtre 48h.
  const incidentSignalAllowed = isRunOpenForMenageToday(run, today) || withinIncidentWindow

  return (
    <article className="fbz-card" style={{background:DS.card,borderRadius:14,border:`0.5px solid ${DS.border}`,boxShadow:'0 1px 2px rgba(33,83,135,.05)',padding:'16px 18px',position:'relative'}}>
      {/* LOT FRISE CONTRÔLE — la pastille œil, ABSOLUE au coin de la carte :
          rien du contenu existant ne bouge. Rendue SEULEMENT si la donnée
          contrôle est fournie en prop (défaut absent = rien). */}
      {controle && (
        <PastilleControle statut={controle.statut}
          onOuvrir={onOuvrirControle ? () => onOuvrirControle(controle) : undefined}
          style={{position:'absolute',top:-7,right:10,zIndex:2}}
          testId={`pastille-controle-carte-${run.id}`} />
      )}
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,flexWrap:'wrap'}}>
        <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,padding:'4px 9px',borderRadius:7,background:eff.bg,color:eff.fg,fontWeight:700,fontFamily:DS.fontBody}}
          title={eff.key === 'anomalie' ? 'Run terminé avant la date prévue — vérifier' : pill.label}>
          <pill.Ico size={13} strokeWidth={2.1} />{pill.label}
        </span>
        {/* Badge agent (orchestrateur uniquement) : « Moi » pour ses propres runs,
            l'email de l'agent (repli faute de prénom) sinon. */}
        {showAgentBadge && (
          <span
            style={{
              display:'inline-flex',alignItems:'center',gap:5,
              fontSize:10.5,padding:'4px 9px',borderRadius:7,fontWeight:700,fontFamily:DS.fontBody,
              background: isOwnRun ? DS.cielSoft : DS.toolBg,
              color: isOwnRun ? DS.marine : DS.textSecondary,
              border: `1px solid ${isOwnRun ? `${DS.ciel}66` : DS.border}`,
              maxWidth:'100%',overflow:'hidden',
            }}
            title={isOwnRun ? 'Ton propre ménage' : `Agent : ${run.assigned_provider_email}`}>
            {isOwnRun ? <><User size={12} strokeWidth={2.1} />Moi</> : <><Users size={12} strokeWidth={2.1} /><span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{run.assigned_provider_email}</span></>}
          </span>
        )}
        {/* Repère d'EXCEPTION — il NOMME l'agent que la règle aurait choisi.
            Un badge disant seulement « exception » obligerait à ouvrir la fiche
            du logement pour savoir à quoi on déroge, ce qui annule son intérêt. */}
        {exception && (
          <span
            data-testid={`exception-${run.id}`}
            title={`Ce ménage déroge à la règle du logement. ${exception}`}
            style={{
              display:'inline-flex',alignItems:'center',gap:5,
              fontSize:10.5,padding:'4px 9px',borderRadius:7,fontWeight:700,fontFamily:DS.fontBody,
              background: DS.corailSoft, color: DS.corailText,
              border: `1px solid ${DS.corail}66`,
              maxWidth:'100%',overflow:'hidden',
            }}>
            <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{exception}</span>
          </span>
        )}
        {/* ADR-302 — pastille discrète « N consigne(s) ». Le détail se lit dans
            la checklist (là où l'agent regarde avant d'intervenir) ; ici on
            signale seulement qu'il y a quelque chose à lire. 0 → aucun rendu. */}
        {instructionsCount > 0 && (
          <span
            data-testid="runcard-consignes-badge"
            title={`${instructionsCount} consigne${instructionsCount > 1 ? 's' : ''} interne${instructionsCount > 1 ? 's' : ''} — détail dans la checklist`}
            style={{
              display:'inline-flex',alignItems:'center',gap:5,
              fontSize:10.5,padding:'4px 9px',borderRadius:7,fontWeight:700,fontFamily:DS.fontBody,
              background:DS.corailSoft,color:DS.corailText,border:`1px solid ${DS.corail}44`,
            }}>
            <Info size={12} strokeWidth={2.1} />
            {instructionsCount} consigne{instructionsCount > 1 ? 's' : ''}
          </span>
        )}
      </div>
      {/* NOM lisible du logement. Repli en cascade : info live (endpoint) →
          `logementNameFallback` (résolu du cfg par l'appelant, jamais un code) →
          id brut en tout dernier recours (ne devrait jamais s'afficher si le cfg
          connaît le logement). Prop additive : les appelants qui ne la passent
          pas gardent le comportement historique (info?.nom || id). */}
      <div style={{fontFamily:DS.fontDisplay,fontSize:19,fontWeight:600,color:DS.marine,marginBottom:4,lineHeight:1.2}}>
        {info?.nom || logementNameFallback || run.logement_id}
      </div>
      {/* PLANNING FIXÉ PAR L'ORCHESTRATEUR — l'agent n'organise pas, il exécute :
          encore faut-il qu'il voie l'heure qu'on lui a donnée et le temps prévu.
          Absent quand personne n'a rien fixé : afficher « — » ferait croire à un
          réglage manquant alors que le créneau suffit. */}
      <PlanningFixe run={run} timing={info?.timing} />
      <GuestCountLine run={run} />
      {/* À APPORTER — le manque du ménage précédent, VU avant de partir. */}
      <BlocAApporter produits={info?.a_apporter} testId={`a-apporter-${run.id}`} />
      {/* Heures RÉELLES voyageur : ORCHESTRATEUR uniquement. */}
      {isOrchestrateur && <GuestRealTimesLine times={guestRealTimes} />}

      <AccessSection info={info} loading={loading} error={error} />
      {/* Horaires THÉORIQUES cfg (info.timing). */}
      <TimingSection timing={info?.timing} />

      {/* Sélecteur de RÉASSIGNATION (orchestrateur uniquement). */}
      {assignOptions.length > 0 && (
        <ReassignSelector run={run} assignOptions={assignOptions} onReassign={onReassign} />
      )}
      {/* Retour à la règle. Trois branches : bouton, refus EXPLIQUÉ, ou rien —
          jamais un bouton qui échouerait (on croirait avoir corrigé). */}
      {retour && retour.possible && (
        <>
          <button
            type="button"
            data-testid={`retour-regle-${run.id}`}
            className="fbz-press"
            onClick={() => setConfirmRetour(true)}
            style={{
              marginTop:10,display:'inline-flex',alignItems:'center',gap:6,
              padding:'7px 12px',borderRadius:DS.radiusControl,
              border:`1px solid ${DS.ciel}88`,background:DS.cielSoft,color:DS.marine,
              fontSize:12,fontWeight:700,fontFamily:DS.fontBody,cursor:'pointer',
            }}>
            <RotateCcw size={13} strokeWidth={2.1} /> Revenir à la règle
          </button>
          {confirmRetour && (
            <FeuilleConfirmation
              testId={`retour-regle-confirm-${run.id}`}
              titre="Rendre ce ménage à l'agent de la règle ?"
              texte={`${nomAgent ? (nomAgent(retour.cible) || retour.cible) : retour.cible} reprendra ce ménage à votre place. L'exception posée sur ce ménage disparaît ; la règle du logement continue de s'appliquer.`}
              libelleConfirmer="Rendre le ménage"
              libelleRenoncer="Le garder"
              ton="action"
              onConfirmer={() => { setConfirmRetour(false); onReassign(run, retour.cible) }}
              onRenoncer={() => setConfirmRetour(false)} />
          )}
        </>
      )}
      {retour && !retour.possible && (
        <div
          data-testid={`retour-regle-impossible-${run.id}`}
          style={{marginTop:10,fontSize:11.5,color:DS.textMuted,lineHeight:1.4}}>
          {retour.raison}
        </div>
      )}

      {/* Bandeau fenêtre de signalement tardive 48h (run completed récent). */}
      {withinIncidentWindow && (
        <div style={{marginTop:10,display:'flex',gap:8,alignItems:'flex-start',padding:'9px 12px',background:DS.cielSoft,color:DS.marineDeep,borderRadius:8,fontSize:12,lineHeight:1.5,border:`0.5px solid ${DS.ciel}55`,borderLeft:`3px solid ${DS.ciel}`}}>
          <Clock size={15} strokeWidth={2} style={{flexShrink:0,marginTop:1}} />
          <span>Tu as encore <strong>{hoursRemaining}h</strong> pour signaler un incident sur ce ménage.</span>
        </div>
      )}

      <div style={{display:'flex',gap:10,marginTop:14,flexWrap:'wrap'}}>
        <button onClick={onOpenChecklist} className="fbz-press fbz-tap"
          style={{flex:'2 1 auto',display:'inline-flex',alignItems:'center',justifyContent:'center',gap:8,background:DS.corail,color:'#fff',border:'none',borderRadius:DS.radiusControl,padding:'12px 16px',fontSize:13.5,fontWeight:700,cursor:'pointer',fontFamily:DS.fontBody}}>
          <ClipboardList size={17} strokeWidth={2} /> Voir la checklist
        </button>
        <button onClick={() => incidentSignalAllowed && onSignalIncident(eff)}
          disabled={!incidentSignalAllowed}
          className="fbz-press fbz-tap"
          title={!incidentSignalAllowed ? 'Signalement disponible le jour du ménage' : ''}
          style={{
            flex:'1 1 auto',
            display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7,
            background: incidentSignalAllowed ? DS.card : DS.toolBg,
            color: incidentSignalAllowed ? DS.marine : DS.textMuted,
            border:`1px solid ${incidentSignalAllowed ? DS.borderHover : DS.border}`,
            borderRadius:DS.radiusControl,
            padding:'12px 16px',
            fontSize:13,
            fontWeight:700,
            cursor: incidentSignalAllowed ? 'pointer' : 'not-allowed',
            fontFamily:DS.fontBody,
          }}>
          {incidentSignalAllowed
            ? <><AlertTriangle size={16} strokeWidth={2} /> Signaler un incident</>
            : <><Lock size={15} strokeWidth={2} /> Signalement le jour J</>}
        </button>
        {/* LOT FRISE CONTRÔLE — « Contrôler » (orchestrateur), 44 px de cible.
            Visible SEULEMENT si : la prop onControler est fournie (défaut
            absent = rien), le ménage est FINI, encore dans la fenêtre RÉELLE
            (contrat + 12 h — recalage 22/08), et SANS contrôle déjà déposé. Le câblage (navigation vers l'onglet
            Contrôle) appartient à l'appelant — ici on appelle onControler(run). */}
        {onControler && run.status === 'completed' && !controle
          && dansFenetreControle(run.scheduled_date, heureContrat) && (
          <button type="button" data-testid={`controler-${run.id}`}
            onClick={() => onControler(run)}
            className="fbz-press fbz-tap"
            style={{
              flex:'1 1 auto',minHeight:44,
              display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7,
              background:DS.card,color:DS.marine,
              border:`1px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,
              padding:'12px 16px',fontSize:13,fontWeight:700,cursor:'pointer',
              fontFamily:DS.fontBody,
            }}>
            <Eye size={16} strokeWidth={2} /> Contrôler
          </button>
        )}
      </div>
    </article>
  )
}

// ── ReassignSelector ────────────────────────────────────────────────
// Sélecteur « Assigner à » (orchestrateur). Rendu uniquement quand assignOptions
// est non vide (cf. RunCard). Logique inchangée (confirmation native + onReassign).
export function ReassignSelector({ run, assignOptions, onReassign }) {
  // ORPHELIN (m11, migration 20261119000001) : un run sans agent a
  // assigned_provider_email = null. Un <select value={null}> déclenche le
  // warning React « value prop on select should not be null » — on normalise
  // en chaîne vide et l'option vide « Non assigné » est rendue explicitement.
  const current = run.assigned_provider_email || ''
  const locked = run.status === 'completed'

  const labelFor = (email) => {
    const opt = assignOptions.find(o => o.value === email)
    if (opt) return opt.label
    return email || 'Non assigné'
  }

  // ⚠️ NOTRE GRAMMAIRE, plus une boîte du navigateur (Dimitri 2026-08-22).
  const [confirmCible, setConfirmCible] = useState(null)
  const onChange = (e) => {
    const next = e.target.value
    e.target.value = current // reset visuel : la vérité vient du reload
    if (!next || next === current) return
    setConfirmCible(next)
  }

  return (
    <div style={{marginTop:12,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
      {confirmCible && (
        <FeuilleConfirmation
          testId={`reassign-confirm-${run.id}`}
          titre="Confier ce ménage à quelqu'un d'autre ?"
          texte={`Ce ménage passe de ${labelFor(current)} à ${labelFor(confirmCible)}. Les deux agents verront le changement dans leur journée.`}
          libelleConfirmer={`Confier à ${labelFor(confirmCible)}`}
          libelleRenoncer="Ne rien changer"
          ton="action"
          onConfirmer={() => { const c = confirmCible; setConfirmCible(null); onReassign(run, c) }}
          onRenoncer={() => setConfirmCible(null)} />
      )}
      <label style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12,color:DS.textSecondary,fontWeight:600}}>
        <Users size={14} strokeWidth={2} color={DS.ciel} /> Assigner à
      </label>
      <select
        value={current}
        onChange={onChange}
        disabled={locked}
        className="fbz-tap"
        title={locked ? 'Ménage terminé — réassignation impossible' : 'Réassigner ce ménage'}
        style={{
          flex:'1 1 auto',
          minWidth:160,
          fontSize:13,
          padding:'8px 10px',
          borderRadius:DS.radiusControl,
          border:`1px solid ${DS.border}`,
          background: locked ? DS.toolBg : DS.card,
          color: locked ? DS.textMuted : DS.marine,
          fontFamily:DS.fontBody,
          cursor: locked ? 'not-allowed' : 'pointer',
        }}>
        {!assignOptions.some(o => o.value === current) && (
          <option value={current}>{labelFor(current)}</option>
        )}
        {assignOptions.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

// ── GuestCountLine ──────────────────────────────────────────────────
// L'agent ménage lit le nb de voyageurs depuis le snapshot du run. Rien rendu si
// guest_count absent. RGPD : aucune PII.
export function GuestCountLine({ run }) {
  const guestCount = run?.guest_count
  if (guestCount == null) return null
  return (
    <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12.5,color:DS.textSecondary,marginBottom:10}}>
      <Users size={14} strokeWidth={2} color={DS.ciel} />
      <span>{guestCount} {guestCount > 1 ? 'voyageurs' : 'voyageur'}</span>
    </div>
  )
}

// ── GuestRealTimesLine ──────────────────────────────────────────────
// Heures RÉELLES du voyageur, ORCHESTRATEUR UNIQUEMENT. `times` = { arrival,
// departure } | null. Rien rendu si les deux sont absents.
export function GuestRealTimesLine({ times }) {
  const hm = (t) => (t ? String(t).slice(0, 5) : null)
  const arrival = hm(times?.arrival) || null
  const departure = hm(times?.departure) || null
  if (!arrival && !departure) return null
  return (
    <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:DS.marine,marginBottom:10,fontWeight:600}}>
      <Clock size={14} strokeWidth={2} />
      <span>Heure réelle voyageur — Départ {departure || '—'} · Arrivée {arrival || '—'}</span>
    </div>
  )
}

// ── AccessSection ───────────────────────────────────────────────────
// Infos accès logement (adresse + 5 codes structurés). Codes EN GROS pour lecture
// mobile sur place (le code « Boîte à clés » = info critique à l'arrivée).
// Utilisé par RunCard + ChecklistRunModal.
export function AccessSection({ info, loading, error, compact = false }) {
  if (loading) {
    return (
      <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 12px',background:DS.toolBg,borderRadius:8,fontSize:11.5,color:DS.textSecondary,fontStyle:'italic'}}>
        <Clock size={14} strokeWidth={2} /> Chargement infos accès…
      </div>
    )
  }
  if (error) {
    return (
      <div style={{display:'flex',gap:8,alignItems:'flex-start',padding:'10px 12px',background:DS.dangerBg,color:DS.dangerText,borderRadius:8,fontSize:11.5,lineHeight:1.5}}>
        <CircleAlert size={15} strokeWidth={2} style={{flexShrink:0,marginTop:1}} /> Infos accès indisponibles : {error}
      </div>
    )
  }
  if (!info) return null

  const adresseLine = [info.adresse_complete, [info.code_postal, info.ville].filter(Boolean).join(' ')]
    .filter(s => s && String(s).trim().length > 0).join(' · ')
  const codes = info.codes_acces || {}
  const codeItems = [
    { label: "Porte d'entrée", value: codes.porte_entree },
    { label: 'Boîte à clés',   value: codes.boite_cles },
    { label: 'Interphone',     value: codes.interphone },
    { label: 'Coffre',         value: codes.coffre },
    { label: 'Garage',         value: codes.garage },
  ].filter(it => it.value && String(it.value).trim().length > 0)

  if (!adresseLine && codeItems.length === 0) {
    return (
      <div style={{display:'flex',gap:8,alignItems:'flex-start',padding:'10px 12px',background:DS.warningBg,borderRadius:8,fontSize:11.5,color:DS.warningText,lineHeight:1.5,marginTop:8,borderLeft:`3px solid ${DS.warning}`}}>
        <Info size={15} strokeWidth={2} style={{flexShrink:0,marginTop:1}} /> Aucun code d'accès configuré pour ce logement (admin à contacter si besoin).
      </div>
    )
  }

  return (
    <div style={{padding: compact ? '10px 12px' : '12px 14px',background:DS.toolBg,borderRadius:10,border:`0.5px solid ${DS.toolBorder}`,marginTop:8,marginBottom: compact ? 0 : 4}}>
      {adresseLine && (
        <div style={{display:'flex',gap:7,alignItems:'flex-start',fontSize:12.5,color:DS.textPrimary,lineHeight:1.5,marginBottom: codeItems.length ? 11 : 0}}>
          <MapPin size={15} strokeWidth={2} color={DS.marine} style={{flexShrink:0,marginTop:1}} />
          <span>{adresseLine}</span>
        </div>
      )}
      {codeItems.length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'auto 1fr',rowGap:9,columnGap:12,alignItems:'center'}}>
          {codeItems.map(it => (
            <Fragment key={it.label}>
              <div style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12,color:DS.textSecondary}}>
                <Key size={14} strokeWidth={2} color={DS.ciel} /> {it.label}
              </div>
              <div style={{fontFamily:DS.fontDisplay,fontSize:20,fontWeight:600,color:DS.marine,letterSpacing:'.5px',lineHeight:1.1}}>
                {it.value}
              </div>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

// ── TimingSection ───────────────────────────────────────────────────
// PR #151 PARTIE F (ADR-085) — plage horaire ménage : check-out précédent →
// Heure fixée + durée prévue, telles que l'orchestrateur les a posées dans la
// frise. `duree_min` absent ⇒ on ne devine pas : on n'affiche que ce qui a été
// décidé. La durée par défaut du logement n'est pas lisible ici (elle vit dans
// `fbz8-cfg`, refusé aux rôles terrain par la RLS) — l'agent voit donc la durée
// quand elle a été explicitement posée pour SON ménage.
/**
 * QUAND Y ALLER, ET COMBIEN DE TEMPS. Rien d'autre.
 *
 * ⚠️ CE BLOC A ÉTÉ DÉGRAISSÉ (décision Dimitri 2026-08-12). Il portait
 * « fixé par l'orchestrateur », « 4 h au lieu de 1 h 30 », et un avertissement
 * quand l'heure devenait impossible. C'était demander à l'agent d'arbitrer
 * entre deux consignes puis de faire le facteur — alors qu'il a besoin d'une
 * seule chose : l'heure à laquelle se présenter.
 *
 * ⚠️ L'HEURE AFFICHÉE EST RECALÉE. Un horaire accordé après coup peut rendre
 * l'heure fixée impossible : on la ramène au début de la plage, silencieusement
 * ICI. Le signalement, lui, remonte à l'orchestrateur — sa ligne passe dans
 * « À régler » de la frise avec le motif « heure recalée à 13h ». C'est lui qui
 * décide, c'est lui qui doit savoir.
 *
 * ⚠️ LA DURÉE EST BRUTE, sans comparaison au défaut du logement : l'agent n'a
 * pas à connaître l'habitude d'un logement qu'il ne gère pas. L'exception reste
 * dite dans la frise, à qui elle sert.
 */
export function PlanningFixe({ run, timing }) {
  const duree = Number(run?.duree_min) > 0 ? Number(run.duree_min) : null
  const { debut } = heureEffective({
    heureFixee: run?.heure_fixee,
    dureeMin: duree,
    plageDebut: timing?.checkout_time,
    plageFin: timing?.checkin_time,
  })
  if (debut == null && !duree) return null
  return (
    <div data-testid={`planning-fixe-${run?.id}`}
      style={{display:'inline-flex',alignItems:'center',gap:7,marginBottom:6,
        padding:'5px 10px',borderRadius:8,background:DS.cielSoft,color:DS.marine,
        fontSize:12.5,fontWeight:700,fontFamily:DS.fontBody}}>
      <Clock size={14} strokeWidth={2.2} style={{flexShrink:0}} />
      {debut != null ? `À ${formaterHeure(debut)}` : 'Durée prévue'}
      {duree ? ` · ${formaterDuree(duree)}` : ''}
    </div>
  )
}

// check-in suivant + durée disponible. Aide à anticiper la pression timing.
export function TimingSection({ timing }) {
  if (!timing) return null
  const { checkout_time: co, checkin_time: ci, duration_min: dur } = timing
  // La RAISON d'une borne décalée, servie par le guichet avec l'heure elle-même.
  const raison = timing.accorde_raison || null
  if (!co && !ci) return null

  const fmtHM = (hm) => hm ? String(hm).slice(0, 5) : '—'
  const fmtDuration = (min) => {
    const h = Math.floor(min / 60)
    const m = min % 60
    return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`
  }

  // Anomalie horaires (checkin avant checkout — donnée incohérente)
  if (co && ci && typeof dur === 'number' && dur < 0) {
    return (
      <div style={{display:'flex',gap:8,alignItems:'flex-start',padding:'10px 12px',background:DS.dangerBg,color:DS.dangerText,borderRadius:8,fontSize:12,marginTop:8,lineHeight:1.5}}>
        <AlertTriangle size={15} strokeWidth={2} style={{flexShrink:0,marginTop:1}} />
        <span>Anomalie horaires : check-in <strong>{fmtHM(ci)}</strong> avant check-out <strong>{fmtHM(co)}</strong> (écart {fmtDuration(Math.abs(dur))}). Vérifier avec admin.</span>
      </div>
    )
  }

  // Cas nominal : 2 horaires + duration_min ≥ 0
  if (co && ci && typeof dur === 'number') {
    const tendu = dur < 120 // M-6 strict
    return (
      <div style={{display:'flex',gap:8,alignItems:'flex-start',padding:'10px 12px',background: tendu ? DS.warningBg : DS.toolBg,borderRadius:8,fontSize:12,marginTop:8,lineHeight:1.5,color: tendu ? DS.warningText : DS.textPrimary,border:`0.5px solid ${tendu ? DS.warning : DS.toolBorder}`}}>
        <Clock size={15} strokeWidth={2} style={{flexShrink:0,marginTop:1}} />
        <span>Check-out <strong>{fmtHM(co)}</strong> → Check-in <strong>{fmtHM(ci)}</strong> ({fmtDuration(dur)} disponible{dur >= 60 ? 's' : ''})
          {tendu && <span style={{marginLeft:6,fontWeight:700}}>· Plage tendue ({fmtDuration(dur)})</span>}
          {/* ⚠️ UNE BORNE DÉCALÉE DIT POURQUOI. Sans ce rappel, l'agent lit un
              « 13:00 » qui contredit ce qu'il sait du logement — et une heure
              qu'on ne s'explique pas est une heure qu'on croit fausse, donc
              qu'on ignore. C'est précisément l'heure qu'il ne faut pas ignorer. */}
          {raison && (
            <span data-testid="timing-accorde" style={{display:'block',marginTop:3,fontWeight:700}}>
              {raison}
            </span>
          )}
        </span>
      </div>
    )
  }

  // Checkout seul (pas de check-in suivant)
  if (co && !ci) {
    return (
      <div style={{display:'flex',gap:8,alignItems:'flex-start',padding:'10px 12px',background:DS.toolBg,borderRadius:8,fontSize:12,marginTop:8,lineHeight:1.5,color:DS.textPrimary,border:`0.5px solid ${DS.toolBorder}`}}>
        <Clock size={15} strokeWidth={2} style={{flexShrink:0,marginTop:1}} />
        <span>Check-out <strong>{fmtHM(co)}</strong> (pas de check-in suivant — accès libre après)</span>
      </div>
    )
  }

  // Checkin seul (pas de checkout précédent)
  if (!co && ci) {
    return (
      <div style={{display:'flex',gap:8,alignItems:'flex-start',padding:'10px 12px',background:DS.toolBg,borderRadius:8,fontSize:12,marginTop:8,lineHeight:1.5,color:DS.textPrimary,border:`0.5px solid ${DS.toolBorder}`}}>
        <Clock size={15} strokeWidth={2} style={{flexShrink:0,marginTop:1}} />
        <span>Check-in <strong>{fmtHM(ci)}</strong> (accès libre dès le matin)</span>
      </div>
    )
  }

  return null
}
